/**
 * PortalAssets — 3D model gallery + uploader.
 *
 * Users upload Revit / IFC / DWG / NWD / OBJ / 3DM / STEP files. The
 * backend pushes them into APS OSS, submits a translation job, and
 * polls until SVF2 derivatives are ready. This page shows live
 * translation progress and opens an inline viewer + AI extraction
 * drawer (BoQ / Schedule / Q&A / Validation) when a model is ready.
 */
import { useEffect, useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { Upload, Cuboid, Loader2, CheckCircle2, AlertTriangle, Trash2, RefreshCw, Sparkles, FileText, Calendar } from 'lucide-react';
import { toast } from 'sonner';
import { portalAssetsApi } from '../../services/api';
import ApsViewerPanel from '../../components/instrument/ApsViewerPanel';

const ACCEPTED_EXT = '.rvt,.rfa,.rte,.ifc,.dwg,.dxf,.dwf,.dwfx,.nwd,.nwc,.nwf,.3dm,.obj,.fbx,.gltf,.glb,.stp,.step,.iges,.igs,.skp,.f3d,.iam,.ipt,.sldprt,.sldasm,.max,.prt';

const STATUS_TONE = {
  uploading:   { label: 'Uploading',  dot: 'bg-blue-400',     pill: 'border-blue-500/30 text-blue-300 bg-blue-500/10' },
  pending:     { label: 'Queued',     dot: 'bg-amber-400',    pill: 'border-amber-500/30 text-amber-300 bg-amber-500/10' },
  inprogress:  { label: 'Translating', dot: 'bg-blue-400',    pill: 'border-blue-500/30 text-blue-300 bg-blue-500/10' },
  success:     { label: 'Ready',       dot: 'bg-emerald-400', pill: 'border-emerald-500/30 text-emerald-300 bg-emerald-500/10' },
  failed:      { label: 'Failed',      dot: 'bg-red-400',     pill: 'border-red-500/30 text-red-300 bg-red-500/10' },
  timeout:     { label: 'Timed out',   dot: 'bg-red-400',     pill: 'border-red-500/30 text-red-300 bg-red-500/10' },
};
const tone = (s) => STATUS_TONE[(s || 'pending').toLowerCase()] || STATUS_TONE.pending;

const formatSize = (b) => {
  if (!b) return '—';
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(0)} KB`;
  return `${(b / 1024 / 1024).toFixed(1)} MB`;
};

export default function PortalAssets() {
  const qc = useQueryClient();
  const fileRef = useRef(null);
  const [openAsset, setOpenAsset] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadPct, setUploadPct] = useState(0);

  const { data: assets = [], isLoading } = useQuery({
    queryKey: ['portal-assets'],
    queryFn: () => portalAssetsApi.list().then((r) => r.data?.assets || []),
    // Poll every 6s while any asset is still translating so the UI
    // shows real-time progress without a manual refresh.
    refetchInterval: (data) => {
      const arr = Array.isArray(data) ? data : data?.state?.data || [];
      const anyPending = (arr || []).some((a) => ['uploading', 'pending', 'inprogress'].includes((a.status || '').toLowerCase()));
      return anyPending ? 6000 : false;
    },
  });

  const uploadMut = useMutation({
    mutationFn: (file) => portalAssetsApi.upload(file, (e) => {
      if (e.total) setUploadPct(Math.round((e.loaded / e.total) * 100));
    }),
    onSuccess: () => {
      toast.success('Upload complete — translation started');
      setUploading(false); setUploadPct(0);
      qc.invalidateQueries({ queryKey: ['portal-assets'] });
    },
    onError: (err) => {
      const msg = err?.response?.data?.detail || err?.message || 'Upload failed';
      toast.error(msg);
      setUploading(false); setUploadPct(0);
    },
  });

  const refreshMut = useMutation({
    mutationFn: (id) => portalAssetsApi.refresh(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['portal-assets'] }),
  });

  const deleteMut = useMutation({
    mutationFn: (id) => portalAssetsApi.remove(id),
    onSuccess: () => { toast.success('Asset deleted'); qc.invalidateQueries({ queryKey: ['portal-assets'] }); },
    onError: () => toast.error('Failed to delete asset'),
  });

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    // No client-side size cap. Backend streams to disk + chunked S3
    // multipart upload, so files up to ~25 GB work transparently.
    setUploading(true); setUploadPct(0);
    uploadMut.mutate(file);
    if (fileRef.current) fileRef.current.value = '';
  };

  // Auto-open the freshly-finished asset on first success
  useEffect(() => {
    if (!openAsset) return;
    const fresh = (assets || []).find((a) => a.id === openAsset.id);
    if (fresh && fresh.status !== openAsset.status) {
      setOpenAsset(fresh);
    }
  }, [assets, openAsset]);

  return (
    <div className="max-w-6xl mx-auto px-4 py-6" data-testid="portal-assets-page">
      {/* Header */}
      <div className="mb-6 flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="ig-eyebrow text-[10px] text-nexus-accent-blue">3D ASSETS</div>
          <h1 className="font-heading text-2xl md:text-3xl font-bold text-nexus-text-primary mt-1">Model Library</h1>
          <p className="text-[13px] text-nexus-text-secondary mt-1 max-w-2xl">
            Upload Revit / IFC / DWG / NWD / OBJ / 3DM / STEP and let Cognara extract Bills of Quantities, generate schedules, audit data quality, and answer questions element-by-element.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg text-[13px] font-semibold text-white transition-all disabled:opacity-60"
            style={{ background: 'linear-gradient(135deg, #1E4E8C 0%, #2563EB 100%)' }}
            data-testid="upload-asset-btn"
          >
            {uploading ? <Loader2 className="animate-spin" size={14} /> : <Upload size={14} />}
            {uploading ? `Uploading ${uploadPct}%` : 'Upload 3D Model'}
          </button>
          <input
            ref={fileRef}
            type="file"
            accept={ACCEPTED_EXT}
            onChange={handleFile}
            className="hidden"
            data-testid="upload-asset-input"
          />
        </div>
      </div>

      {/* Empty state */}
      {!isLoading && assets.length === 0 && (
        <div className="rounded-2xl border border-dashed border-nexus-border-subtle p-12 text-center"
             style={{ background: 'var(--layer-0)' }} data-testid="assets-empty">
          <Cuboid size={42} className="mx-auto text-nexus-text-tertiary mb-3" />
          <h3 className="text-[16px] font-semibold text-nexus-text-primary">No models yet</h3>
          <p className="text-[13px] text-nexus-text-secondary mt-1 max-w-md mx-auto">
            Drop a Revit, IFC, or DWG to see Cognara light up. Translation usually takes 1–5 minutes for typical AEC files.
          </p>
          <div className="mt-5 flex items-center justify-center gap-3 text-[11px] text-nexus-text-tertiary">
            <span className="inline-flex items-center gap-1"><FileText size={11} /> Auto-BoQ</span>
            <span className="inline-flex items-center gap-1"><Calendar size={11} /> Auto-schedule</span>
            <span className="inline-flex items-center gap-1"><Sparkles size={11} /> Element Q&A</span>
          </div>
        </div>
      )}

      {/* Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {assets.map((asset) => {
          const t = tone(asset.status);
          const ready = asset.status === 'success';
          const elements = asset.element_summary?.total_elements;
          return (
            <motion.div
              key={asset.id}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              className="rounded-xl border border-nexus-border-subtle overflow-hidden flex flex-col"
              style={{ background: 'var(--layer-1)' }}
              data-testid={`asset-card-${asset.id}`}
            >
              {/* Preview block (placeholder gradient — we could show APS thumbnail when ready) */}
              <div className="h-32 relative flex items-center justify-center"
                style={{ background: 'linear-gradient(135deg, rgba(30,78,140,0.25), rgba(242,107,31,0.18))' }}>
                <Cuboid size={42} className="text-white/70" />
                <div className="absolute top-2 right-2 inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-medium border backdrop-blur-md" 
                  style={{ borderColor: 'rgba(255,255,255,0.15)', background: 'rgba(0,0,0,0.35)' }}>
                  <span className={`w-1.5 h-1.5 rounded-full ${t.dot} ${(asset.status === 'pending' || asset.status === 'inprogress') ? 'animate-pulse' : ''}`} />
                  <span className="text-white">{t.label}</span>
                </div>
              </div>
              <div className="p-3 flex-1 flex flex-col">
                <div className="text-[13px] font-semibold text-nexus-text-primary truncate" title={asset.filename}>
                  {asset.filename}
                </div>
                <div className="text-[11px] text-nexus-text-tertiary mt-0.5">
                  {formatSize(asset.size)} · {asset.ext?.replace('.', '').toUpperCase()}
                  {ready && elements ? ` · ${elements.toLocaleString()} elements` : ''}
                  {!ready && asset.progress ? ` · ${asset.progress}` : ''}
                </div>
                <div className="mt-3 flex items-center gap-1.5">
                  <button
                    onClick={() => setOpenAsset(asset)}
                    disabled={!ready}
                    className="flex-1 px-3 py-1.5 text-[11.5px] font-semibold rounded-md text-white disabled:opacity-40 disabled:cursor-not-allowed"
                    style={{ background: ready ? 'linear-gradient(135deg, #1E4E8C, #2563EB)' : 'rgba(148,163,184,0.25)' }}
                    data-testid={`open-asset-${asset.id}`}
                  >
                    {ready ? 'Open viewer · AI' : tone(asset.status).label}
                  </button>
                  {(asset.status === 'pending' || asset.status === 'inprogress' || asset.status === 'failed') && (
                    <button
                      onClick={() => refreshMut.mutate(asset.id)}
                      className="p-1.5 rounded-md text-nexus-text-secondary hover:bg-nexus-bg-tertiary/40"
                      title="Refresh status"
                    >
                      <RefreshCw size={13} />
                    </button>
                  )}
                  <button
                    onClick={() => {
                      if (confirm(`Delete ${asset.filename}?`)) deleteMut.mutate(asset.id);
                    }}
                    className="p-1.5 rounded-md text-nexus-text-secondary hover:text-red-400 hover:bg-red-500/10"
                    title="Delete"
                    data-testid={`delete-asset-${asset.id}`}
                  >
                    <Trash2 size={13} />
                  </button>
                </div>
                {asset.status === 'failed' && (
                  <div className="mt-2 text-[10.5px] text-red-300 inline-flex items-center gap-1">
                    <AlertTriangle size={10} /> Translation failed — try re-uploading or check file integrity.
                  </div>
                )}
                {ready && (
                  <div className="mt-2 text-[10.5px] text-emerald-300 inline-flex items-center gap-1">
                    <CheckCircle2 size={10} /> Indexed and ready for AI extraction
                  </div>
                )}
              </div>
            </motion.div>
          );
        })}
      </div>

      <ApsViewerPanel
        asset={openAsset}
        open={!!openAsset}
        onClose={() => setOpenAsset(null)}
      />
    </div>
  );
}
